import React, { useEffect, useState } from 'react';
import { Redirect } from 'react-router-dom';

import AppLoader from '../../components/loading';
import checkoutRouting from '../../services/checkoutRouting';
import checkoutVariant from '../../services/checkoutVariant';
import generalHelpers from '../../services/generalHelpers';

/* Abandoned-cart emails already sent point at /guest/checkout/review, and
   nothing there read the arm — so a unified-arm shopper finished on the 2024
   checkout while the shell still counted them as unified. Held rather than
   swapped, or the wrong checkout shows until Statsig settles. */

// The budget whenVariantResolved uses. Past it an unresolved variant reads as
// the existing checkout, so a dead Statsig leaves these routes as they are.
const RESOLVE_TIMEOUT_MS = 1500;

/* The shell stores only source, medium and campaign, so utm_term and Klaviyo's
   _kx live on this URL and nowhere else — carried across, not dropped. */
const unifiedCheckoutFrom = (search) => {
  const [pathname, stored] = generalHelpers
    .appendStoredUTMs(checkoutRouting.UNIFIED_CHECKOUT_PATH)
    .split('?');
  const params = new URLSearchParams(stored || '');
  new URLSearchParams(search || '').forEach((value, key) => {
    if (!params.has(key)) params.set(key, value);
  });

  const query = params.toString();
  return { pathname, search: query ? `?${query}` : '' };
};

/* A corporate order reaches the review route either in location.state
   (corporate-portal/index.js:91-99) or with only the stored company behind it,
   when the shopper checks out from the cart drawer instead. Both keep the
   checkout that can complete them. */
const isCorporateHandoff = (location) =>
  !!location?.state?.companyCode || checkoutRouting.isCorporateShopper();

const deferToUnifiedCheckout = (WrappedComponent) => {
  const Deferring = (props) => {
    /* Read synchronously so a settled Statsig does not get a loader over a
       ready page. Only once it can answer: a read while Loading returns the
       fallback and logs an exposure for an arm nobody saw. */
    const [variant, setVariant] = useState(() =>
      checkoutVariant.variantIsResolved()
        ? { resolved: true, unified: checkoutVariant.showsUnifiedCheckout() }
        : { resolved: false, unified: false },
    );

    useEffect(() => {
      if (variant.resolved) return undefined;

      const unsubscribe = checkoutVariant.subscribeToVariant(
        ({ showsUnifiedCheckout, resolved }) => {
          if (resolved) setVariant({ resolved, unified: showsUnifiedCheckout });
        },
      );
      const timer = setTimeout(
        () => setVariant({ resolved: true, unified: false }),
        RESOLVE_TIMEOUT_MS,
      );

      return () => {
        unsubscribe();
        clearTimeout(timer);
      };
    }, [variant.resolved]);

    if (!variant.resolved) return <AppLoader />;

    if (variant.unified && !isCorporateHandoff(props.location)) {
      // Replaced, not pushed: Back would otherwise land on the route that
      // bounces, and bounce again.
      return <Redirect to={unifiedCheckoutFrom(props.location?.search)} />;
    }

    // eslint-disable-next-line react/jsx-props-no-spreading
    return <WrappedComponent {...props} />;
  };

  Deferring.displayName = `deferToUnifiedCheckout(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;

  return Deferring;
};

export default deferToUnifiedCheckout;
